// 交易所初筛问卷主组件
// 基于 docs/问卷/交易所初筛问卷设计.md
// 独立布局，不使用 StandardPageLayout

import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  Steps,
  Button,
  Form,
  message,
  Progress,
  Space,
  Typography,
  Spin,
  Alert,
  Row,
  Col,
  Affix,
  Modal,
} from 'antd';
import {
  SaveOutlined,
  SendOutlined,
  LeftOutlined,
  RightOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  useNavigate,
  useParams,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  InitialScreeningQuestionnaire,
  QuestionnaireStatus,
  getQuestionnaireSteps,
  DEFAULT_QUESTIONNAIRE,
} from '../../types/questionnaire';
import { useQuestionnaire } from '../../hooks/useQuestionnaire';
import {
  useCreateQuestionnaireMutation,
  useAutoRegisterWithQuestionnaireMutation,
} from '../../store/api/questionnaireApi';
import { useAppSelector } from '../../store';
import { QuestionnaireStep1 } from './steps/QuestionnaireStep1';
import { QuestionnaireStep2 } from './steps/QuestionnaireStep2';
import { QuestionnaireStep3 } from './steps/QuestionnaireStep3';
import { QuestionnaireStep4 } from './steps/QuestionnaireStep4';
import { QuestionnaireReview } from './QuestionnaireReview';
import CaptchaInput from '../auth/CaptchaInput';
import LanguageSwitcher from '../common/LanguageSwitcher';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

const InitialScreeningQuestionnaireComponent: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { id: paramQuestionnaireId } = useParams<{ id: string }>();
  const [form] = Form.useForm();

  // 获取渠道信息
  const channelCode = searchParams.get('channel') || 'OFFICIAL';
  const [channelInfo, setChannelInfo] = useState<{
    code: string;
    name: string;
  }>({
    code: channelCode,
    name:
      channelCode === 'OFFICIAL'
        ? t('questionnaire:channel.official')
        : channelCode,
  });

  // 检查编辑模式和重新提交模式 - 支持URL参数和location.state
  const urlParams = new URLSearchParams(location.search);
  const editMode =
    location.state?.editMode || urlParams.get('editMode') === 'true';
  const resubmitMode =
    location.state?.resubmitMode || urlParams.get('resubmitMode') === 'true';
  const urlQuestionnaireId = urlParams.get('questionnaireId');
  const projectName = location.state?.projectName;

  // 优先使用URL参数中的问卷ID，然后是路径参数
  const questionnaireId = urlQuestionnaireId || paramQuestionnaireId;

  // 使用问卷Hook
  const {
    questionnaire,
    isLoading,
    hasUnsavedChanges,
    lastSavedAt,
    aiScore,
    validationErrors,
    progress,
    canSubmit,
    updateQuestionnaire,
    saveDraft,
    submitQuestionnaire,
    calculateAIScore,
    isCreating,
    isUpdating,
    isSubmitting,
    isSavingDraft,
    isCalculatingScore,
  } = useQuestionnaire({
    questionnaireId,
    autoSave: true,
    autoSaveInterval: 30000, // 30秒自动保存
  });

  // RTK Query mutations
  const [createQuestionnaire, { isLoading: isSubmittingDirect }] =
    useCreateQuestionnaireMutation();
  const [autoRegisterWithQuestionnaire, { isLoading: isAutoRegistering }] =
    useAutoRegisterWithQuestionnaireMutation();

  // Redux状态
  const { isAuthenticated } = useAppSelector(state => state.auth);

  // 组件状态
  const [currentStep, setCurrentStep] = useState(0);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [showAIScoreModal, setShowAIScoreModal] = useState(false);
  const [autoRegisterResult, setAutoRegisterResult] = useState<any>(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);

  // 验证码状态（未登录用户提交时需要）
  const [captchaId, setCaptchaId] = useState<string>('');
  const [captchaCode, setCaptchaCode] = useState<string>('');

  // 优先使用前端i18n文案，避免英文界面显示后端中文消息
  const resolveLocalizedMessage = useCallback(
    (backendMessage: string | undefined, fallbackKey: string) => {
      if (!backendMessage) return t(fallbackKey);
      const isZh = i18n.language.toLowerCase().startsWith('zh');
      const hasChinese = /[\u4e00-\u9fff]/.test(backendMessage);
      return !isZh && hasChinese ? t(fallbackKey) : backendMessage;
    },
    [i18n.language, t]
  );

  // 获取翻译后的问卷步骤
  const questionnaireSteps = React.useMemo(() => getQuestionnaireSteps(t), [t]);

  // 基于当前步骤计算进度百分比
  const stepProgress = React.useMemo(() => {
    if (isReviewMode) {
      return 100; // 审核模式表示已完成所有步骤
    }
    // 当前步骤的进度 = (当前步骤 + 1) / 总步骤数 * 100
    return Math.round(((currentStep + 1) / questionnaireSteps.length) * 100);
  }, [currentStep, isReviewMode, questionnaireSteps.length]);

  // 同步表单数据到问卷状态
  useEffect(() => {
    if (questionnaire && Object.keys(questionnaire).length > 0) {
      console.log('同步问卷数据到表单:', questionnaire);

      // 确保表单字段正确映射，处理数据库中的完整数据结构
      const formData = {
        // 基本信息
        issuerBasicInfo: questionnaire.issuerBasicInfo || {},

        // 评级和规模信息
        issuerRatingScale: questionnaire.issuerRatingScale || {},

        // 财务状况
        financialStatus: questionnaire.financialStatus || {},

        // 绿色项目信息
        greenProjectInfo: questionnaire.greenProjectInfo || {},

        // 发行计划
        issuancePlan: questionnaire.issuancePlan || {},

        // 风险因素
        riskFactors: questionnaire.riskFactors || {},

        // 联系信息
        contactInfo: questionnaire.contactInfo || {},

        // 补充说明
        additionalInfo: questionnaire.additionalInfo || '',

        // 其他字段
        projectName: questionnaire.projectName,
        status: questionnaire.status,
        id: questionnaire.id || questionnaire._id,
      };

      console.log('设置表单数据:', formData);
      console.log('联系信息数据:', formData.contactInfo);
      form.setFieldsValue(formData);
    }
  }, [questionnaire]); // 移除form依赖，避免无限循环

  // 保存草稿
  const handleSaveDraft = useCallback(async () => {
    try {
      const values = await form.validateFields();
      updateQuestionnaire(values);
      await saveDraft();
    } catch (error) {
      console.error('保存草稿失败:', error);
    }
  }, [form, updateQuestionnaire, saveDraft]);

  // 下一步
  const handleNext = useCallback(async () => {
    try {
      const values = await form.validateFields();

      // 更新问卷数据
      updateQuestionnaire(values);

      // 自动保存当前步骤
      await handleSaveDraft();

      if (currentStep < questionnaireSteps.length - 1) {
        setCurrentStep(prev => prev + 1);
        // 切换步骤后滚动到页面顶部
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // 最后一步，进入审核模式
        setIsReviewMode(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('表单验证失败:', error);
      message.error(t('questionnaire:validation.completeRequired'));
    }
  }, [form, updateQuestionnaire, currentStep, handleSaveDraft]);

  // 上一步
  const handlePrevious = useCallback(() => {
    if (isReviewMode) {
      setIsReviewMode(false);
    } else if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
    // 切换步骤后滚动到页面顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [isReviewMode, currentStep]);

  // 提交问卷
  const handleSubmit = useCallback(async () => {
    try {
      // 未登录用户需要验证码
      if (!isAuthenticated) {
        if (!captchaId || !captchaCode) {
          message.error(t('questionnaire:captcha.required'));
          return;
        }

        // 验证验证码
        try {
          const captchaResponse = await fetch('/api/v1/auth/captcha/verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ captchaId, captchaCode }),
          });
          const captchaResult = await captchaResponse.json();

          if (!captchaResult.success) {
            message.error(
              resolveLocalizedMessage(
                captchaResult.error?.message,
                'questionnaire:captcha.error'
              )
            );
            setCaptchaCode('');
            return;
          }
        } catch {
          message.error(t('questionnaire:captcha.verifyFailed'));
          return;
        }
      }

      // 先保存当前表单数据
      const values = await form.validateFields();
      updateQuestionnaire(values);

      // 准备提交数据 - 对于自动注册保持原始ID，对于已登录用户移除draft ID
      let submitData;
      if (isAuthenticated) {
        // 已登录用户：移除draft ID让后端生成新的唯一ID
        const { _id: draftId, ...questionnaireWithoutId } = questionnaire;
        submitData = {
          ...questionnaireWithoutId,
          ...values,
          status: 'submitted',
          // 添加渠道信息
          channelCode: channelInfo.code,
          channelName: channelInfo.name,
        };
      } else {
        // 未登录用户：保持原始ID以便后续关联
        submitData = {
          ...questionnaire,
          ...values,
          status: 'submitted',
          // 添加渠道信息
          channelCode: channelInfo.code,
          channelName: channelInfo.name,
        };
      }

      console.log('提交问卷数据:', submitData);
      console.log('当前认证状态:', isAuthenticated);

      if (isAuthenticated) {
        // 已登录用户：使用传统方式提交
        console.log('已登录用户提交问卷');
        const result = await createQuestionnaire(submitData).unwrap();

        message.success(t('questionnaire:message.submitSuccess'));

        // 智能导航
        setTimeout(() => {
          const fromMyQuestionnaires =
            location.state?.from === '/issuer/questionnaires' ||
            location.state?.from === '/admin/issuer/questionnaires';

          if (fromMyQuestionnaires) {
            navigate('/admin/issuer/questionnaires');
          } else {
            navigate('/admin/issuer-workbench');
          }
        }, 2000);
      } else {
        // 未登录用户：使用自动注册方式提交
        console.log('未登录用户，启动自动注册流程');

        // 验证必要信息
        if (!submitData.issuerBasicInfo?.issuerName) {
          message.error(t('questionnaire:validation.issuerNameRequired'));
          return;
        }

        if (!submitData.contactInfo?.email) {
          message.error(t('questionnaire:validation.contactEmailRequired'));
          return;
        }

        // 收集设备信息（可选）
        const deviceInfo = {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
        };

        const result = await autoRegisterWithQuestionnaire({
          questionnaire: submitData,
          deviceInfo: deviceInfo,
        }).unwrap();

        console.log('自动注册结果:', result);

        // 保存注册结果用于显示
        setAutoRegisterResult(result);

        // 新用户需要激活，显示激活提示模态框
        if (result.data.registration.isNewUser) {
          setShowRegistrationModal(true);
        } else {
          // 现有用户，显示成功消息并跳转到登录页
          message.success(
            resolveLocalizedMessage(
              result.message,
              'questionnaire:message.submitLinkedExistingAccount'
            )
          );
          setTimeout(() => {
            navigate('/auth/login');
          }, 2000);
        }

        // 注意：不再自动登录，用户需要通过邮件激活后才能登录
      }
    } catch (error) {
      console.error('提交问卷失败:', error);

      // 处理表单验证错误
      if (error && typeof error === 'object' && 'errorFields' in error) {
        const errorFields = (error as any).errorFields;
        if (errorFields && errorFields.length > 0) {
          const firstError = errorFields[0];
          message.error(
            `${t('questionnaire:validation.completeRequired')}：${firstError.errors?.[0] || t('questionnaire:error.formValidationFailed')}`
          );
        } else {
          message.error(t('questionnaire:validation.completeRequired'));
        }
      } else if (
        error &&
        typeof error === 'object' &&
        'status' in error &&
        error.status === 401
      ) {
        // 处理认证错误（已登录用户）
        message.error({
          content: t('questionnaire:error.authExpired'),
          duration: 5,
          onClick: () => {
            // 保存当前问卷数据到localStorage
            const currentData = form.getFieldsValue();
            localStorage.setItem(
              'draftQuestionnaire',
              JSON.stringify(currentData)
            );
            // 跳转到登录页
            window.location.href = '/auth/login';
          },
        });
      } else {
        // 其他错误
        const backendErrorMessage =
          (error as any)?.data?.error?.message || (error as any)?.message;
        const errorMessage = resolveLocalizedMessage(
          backendErrorMessage,
          'questionnaire:error.submitFailed'
        );
        message.error(errorMessage);
      }
    }
  }, [
    form,
    updateQuestionnaire,
    questionnaire,
    createQuestionnaire,
    autoRegisterWithQuestionnaire,
    isAuthenticated,
    navigate,
    location.state,
    channelInfo,
    captchaId,
    captchaCode,
    resolveLocalizedMessage,
    t,
  ]);

  // 计算AI评分
  const handleCalculateAIScore = useCallback(async () => {
    try {
      const values = await form.validateFields();
      updateQuestionnaire(values);

      const result = await calculateAIScore();
      setShowAIScoreModal(true);

      return result;
    } catch (error) {
      console.error('计算AI评分失败:', error);
    }
  }, [form, updateQuestionnaire, calculateAIScore]);

  // 渲染当前步骤内容
  const renderStepContent = () => {
    if (isReviewMode) {
      // 获取表单当前值用于预览
      const formValues = form.getFieldsValue();
      const previewData = {
        ...questionnaire,
        ...formValues,
      };

      // 调试信息
      console.log('预览数据 - questionnaire:', questionnaire);
      console.log('预览数据 - formValues:', formValues);
      console.log('预览数据 - previewData:', previewData);
      console.log('预览数据 - financialStatus:', previewData.financialStatus);

      return <QuestionnaireReview data={previewData} />;
    }

    const stepProps = {
      form,
      questionnaire,
      onUpdate: updateQuestionnaire,
    };

    switch (currentStep) {
      case 0:
        return <QuestionnaireStep1 {...stepProps} />;
      case 1:
        return <QuestionnaireStep2 {...stepProps} />;
      case 2:
        return <QuestionnaireStep3 {...stepProps} />;
      case 3:
        return <QuestionnaireStep4 {...stepProps} />;
      default:
        return null;
    }
  };

  // 渲染底部操作按钮
  const renderActionButtons = () => (
    <Affix offsetBottom={0}>
      <div
        className="questionnaire-action-bar"
        style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderTop: '1px solid rgba(16, 185, 129, 0.1)',
          padding: '16px 24px',
          boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)',
        }}
      >
        {/* 移动端布局 - CSS媒体查询控制显示/隐藏 */}
        <div className="mobile-action-layout">
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              {isReviewMode
                ? t('questionnaire:page.confirmBeforeSubmit')
                : t('questionnaire:page.stepInfo', {
                    current: currentStep + 1,
                    total: questionnaireSteps.length,
                  })}
            </Text>
          </div>

          <Row gutter={[8, 8]} style={{ marginBottom: '12px' }}>
            <Col xs={12}>
              <Button
                icon={<LeftOutlined />}
                onClick={handlePrevious}
                disabled={currentStep === 0 && !isReviewMode}
                size="large"
                block
                style={{ borderRadius: '8px', height: '48px' }}
              >
                {t('questionnaire:button.previous')}
              </Button>
            </Col>
            <Col xs={12}>
              <Button
                icon={<SaveOutlined />}
                onClick={handleSaveDraft}
                loading={isSavingDraft}
                size="large"
                block
                style={{
                  borderRadius: '8px',
                  height: '48px',
                  borderColor: '#10b981',
                  color: '#10b981',
                }}
              >
                {t('questionnaire:button.save')}
              </Button>
            </Col>
          </Row>

          {isReviewMode ? (
            <>
              {/* 未登录用户需要验证码 */}
              {!isAuthenticated && (
                <div style={{ marginBottom: '16px' }}>
                  <Text
                    type="secondary"
                    style={{ display: 'block', marginBottom: '8px' }}
                  >
                    {t('questionnaire:captcha.enterCode')}
                  </Text>
                  <CaptchaInput
                    value={captchaCode}
                    onChange={setCaptchaCode}
                    onCaptchaIdChange={setCaptchaId}
                    placeholder={t('questionnaire:captcha.placeholder')}
                  />
                </div>
              )}
              <Button
                type="primary"
                size="large"
                icon={<SendOutlined />}
                onClick={handleSubmit}
                loading={isSubmittingDirect || isAutoRegistering}
                block
                style={{
                  borderRadius: '8px',
                  height: '52px',
                  fontSize: '18px',
                  fontWeight: '600',
                  background: '#10b981',
                  borderColor: '#10b981',
                  boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                }}
              >
                {t('questionnaire:button.submit')}
              </Button>
            </>
          ) : (
            <Button
              type="primary"
              size="large"
              icon={<RightOutlined />}
              onClick={handleNext}
              block
              style={{
                borderRadius: '8px',
                height: '52px',
                fontSize: '18px',
                fontWeight: '600',
                background: '#10b981',
                borderColor: '#10b981',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
              }}
            >
              {currentStep === questionnaireSteps.length - 1
                ? t('questionnaire:button.enterReview')
                : t('questionnaire:button.next')}
            </Button>
          )}
        </div>

        {/* 桌面端布局 - CSS媒体查询控制显示/隐藏 */}
        <div className="desktop-action-layout">
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <Button
                  icon={<LeftOutlined />}
                  onClick={handlePrevious}
                  disabled={currentStep === 0 && !isReviewMode}
                  style={{ borderRadius: '8px' }}
                >
                  {t('questionnaire:button.previous')}
                </Button>
                <Button
                  icon={<SaveOutlined />}
                  onClick={handleSaveDraft}
                  loading={isSavingDraft}
                  style={{
                    borderRadius: '8px',
                    borderColor: '#10b981',
                    color: '#10b981',
                  }}
                >
                  {t('questionnaire:button.saveDraft')}
                </Button>
              </Space>
            </Col>

            <Col>
              <Space align="center">
                <Text type="secondary">
                  {isReviewMode
                    ? t('questionnaire:page.confirmBeforeSubmit')
                    : t('questionnaire:page.stepInfo', {
                        current: currentStep + 1,
                        total: questionnaireSteps.length,
                      })}
                </Text>

                {isReviewMode ? (
                  <>
                    {/* 未登录用户需要验证码 - 桌面端 */}
                    {!isAuthenticated && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginRight: '16px',
                        }}
                      >
                        <CaptchaInput
                          value={captchaCode}
                          onChange={setCaptchaCode}
                          onCaptchaIdChange={setCaptchaId}
                          placeholder={t('questionnaire:captcha.placeholder')}
                        />
                      </div>
                    )}
                    <Button
                      type="primary"
                      size="large"
                      icon={<SendOutlined />}
                      onClick={handleSubmit}
                      loading={isSubmittingDirect || isAutoRegistering}
                      disabled={false}
                      style={{
                        borderRadius: '8px',
                        background: '#10b981',
                        borderColor: '#10b981',
                        color: '#ffffff',
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                        minWidth: '120px',
                      }}
                    >
                      {t('questionnaire:button.submit')}
                    </Button>
                  </>
                ) : (
                  <Button
                    type="primary"
                    size="large"
                    icon={<RightOutlined />}
                    onClick={handleNext}
                    style={{
                      borderRadius: '8px',
                      background: '#10b981',
                      borderColor: '#10b981',
                      color: '#ffffff',
                      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                      minWidth: '120px',
                    }}
                  >
                    {currentStep === questionnaireSteps.length - 1
                      ? t('questionnaire:button.enterReview')
                      : t('questionnaire:button.next')}
                  </Button>
                )}
              </Space>
            </Col>
          </Row>
        </div>
      </div>
    </Affix>
  );

  // 页面配置
  const title = resubmitMode
    ? t('questionnaire:page.resubmitTitle')
    : editMode
      ? t('questionnaire:page.editTitle')
      : t('questionnaire:page.title');

  const subtitle = resubmitMode
    ? `${t('questionnaire:page.resubmitSubtitle')}${projectName ? ` - ${projectName}` : ''}`
    : editMode
      ? `${t('questionnaire:page.editSubtitle')}${projectName ? ` - ${projectName}` : ''}`
      : t('questionnaire:page.subtitle');

  // 动态生成面包屑导航，支持返回到正确的来源页面
  const breadcrumbs = React.useMemo(() => {
    const baseBreadcrumbs = [
      { label: t('questionnaire:breadcrumb.home'), path: '/' },
    ];

    // 检查用户来源和认证状态
    const token =
      localStorage.getItem('token') || sessionStorage.getItem('token');
    const isAuthenticated = !!token;
    const fromMyQuestionnaires =
      location.state?.from === '/issuer/questionnaires' ||
      location.state?.from === '/admin/issuer/questionnaires';
    const returnTo = location.state?.returnTo;

    if (
      isAuthenticated &&
      (fromMyQuestionnaires || returnTo === 'questionnaire-list')
    ) {
      // 从"我的问卷"页面进入
      baseBreadcrumbs.push(
        {
          label: t('questionnaire:breadcrumb.myQuestionnaires'),
          path: '/admin/issuer/questionnaires',
        },
        { label: t('questionnaire:breadcrumb.fillQuestionnaire'), path: '' }
      );
    } else if (isAuthenticated) {
      // 其他已登录用户
      baseBreadcrumbs.push({
        label: t('questionnaire:breadcrumb.fillQuestionnaire'),
        path: '',
      });
    } else {
      // 未登录用户
      baseBreadcrumbs.push({
        label: t('questionnaire:breadcrumb.fillQuestionnaire'),
        path: '',
      });
    }

    return baseBreadcrumbs;
  }, [location.state, t]);

  const statistics: any[] = [
    // 删除右上角的完成进度及当前步骤显示
  ];

  // 返回上个页面的处理函数
  const handleGoBack = useCallback(() => {
    const token =
      localStorage.getItem('token') || sessionStorage.getItem('token');
    const isAuthenticated = !!token;
    const fromMyQuestionnaires =
      location.state?.from === '/issuer/questionnaires' ||
      location.state?.from === '/admin/issuer/questionnaires';
    const returnTo = location.state?.returnTo;

    if (
      isAuthenticated &&
      (fromMyQuestionnaires || returnTo === 'questionnaire-list')
    ) {
      // 返回到我的问卷列表
      navigate('/admin/issuer/questionnaires');
    } else if (isAuthenticated) {
      // 返回到首页或工作台
      navigate('/');
    } else {
      // 未登录用户返回到登录页面
      navigate('/auth/login');
    }
  }, [navigate, location.state]);

  const actions: any[] = [
    // 移动端删除所有按钮
  ];

  return (
    <div
      className="questionnaire-page"
      style={{
        minHeight: '100vh',
        background: '#f5f5f5',
      }}
    >
      {/* 顶部Banner */}
      <div
        className="questionnaire-header-banner"
        style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          padding: '12px 0 16px 0',
          position: 'relative',
        }}
      >
        <div
          style={{
            width: '100%',
            padding: '0 16px',
          }}
        >
          {/* 第一行：面包屑 + 语言切换 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px',
            }}
          >
            {/* 面包屑 */}
            <div>
              {breadcrumbs.map((crumb, index) => (
                <span
                  key={index}
                  style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '12px',
                  }}
                >
                  {index > 0 && <span style={{ margin: '0 4px' }}>/</span>}
                  {crumb.path ? (
                    <Button
                      type="link"
                      onClick={() => navigate(crumb.path!)}
                      style={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        padding: 0,
                        height: 'auto',
                        fontSize: '12px',
                      }}
                    >
                      {crumb.label}
                    </Button>
                  ) : (
                    <span style={{ color: 'white', fontWeight: '500' }}>
                      {crumb.label}
                    </span>
                  )}
                </span>
              ))}
            </div>
            {/* 语言切换按钮 - 小巧精致 */}
            <LanguageSwitcher
              size="small"
              showLabel={false}
              buttonStyle={{
                background: 'rgba(255, 255, 255, 0.15)',
                borderRadius: '6px',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                color: 'white',
                height: '28px',
                padding: '0 8px',
              }}
            />
          </div>

          {/* 第二行：标题 */}
          <div>
            <h1
              style={{
                color: 'white',
                fontSize: '20px',
                fontWeight: '600',
                margin: 0,
                lineHeight: 1.3,
              }}
            >
              {title}
            </h1>
            {subtitle && (
              <p
                style={{
                  color: 'rgba(255, 255, 255, 0.85)',
                  fontSize: '13px',
                  margin: '4px 0 0 0',
                  lineHeight: 1.4,
                }}
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* 主要内容区域 - 移除最大宽度限制，使用全屏宽度 */}
      <div
        style={{
          width: '100%',
          padding: '24px 24px 100px 24px',
        }}
      >
        {/* 重新提交模式提示 */}
        {resubmitMode && (
          <div style={{ marginBottom: '24px' }}>
            <Alert
              message={t('questionnaire:resubmit.title')}
              description={t('questionnaire:resubmit.description')}
              type="warning"
              showIcon
              style={{
                marginBottom: '16px',
                borderRadius: '12px',
                border: '1px solid #faad14',
                backgroundColor: 'rgba(250, 173, 20, 0.05)',
              }}
            />

            {/* 直接显示审核意见 */}
            <Card
              size="small"
              title={
                <Space>
                  <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                  {t('questionnaire:resubmit.reviewComments')}
                </Space>
              }
              style={{
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(12px)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 193, 7, 0.2)',
                boxShadow: '0 4px 12px rgba(255, 193, 7, 0.1)',
              }}
            >
              {/* 审核意见内容 */}
              {questionnaire?.reviewComments && (
                <div style={{ marginBottom: '12px' }}>
                  <Text strong>
                    {t('questionnaire:resubmit.reviewComments')}：
                  </Text>
                  <Paragraph
                    style={{
                      marginTop: '4px',
                      padding: '8px 12px',
                      background: '#fff7e6',
                      borderRadius: '6px',
                      border: '1px solid #ffd591',
                      marginBottom: '8px',
                    }}
                  >
                    {questionnaire.reviewComments}
                  </Paragraph>
                </div>
              )}

              {/* 拒绝原因 */}
              {questionnaire?.rejectionReasons &&
                questionnaire.rejectionReasons.length > 0 && (
                  <div>
                    <Text strong>
                      {t('questionnaire:resubmit.rejectionReasons')}：
                    </Text>
                    <div style={{ marginTop: '8px' }}>
                      {questionnaire.rejectionReasons.map(
                        (reason: string, index: number) => (
                          <div
                            key={index}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              marginBottom: '6px',
                              padding: '6px 12px',
                              background: '#fff2f0',
                              borderRadius: '6px',
                              border: '1px solid #ffccc7',
                            }}
                          >
                            <span
                              style={{
                                color: '#ff4d4f',
                                marginRight: '8px',
                                fontSize: '12px',
                                lineHeight: '20px',
                              }}
                            >
                              •
                            </span>
                            <Text style={{ fontSize: '14px' }}>{reason}</Text>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

              {/* 如果没有具体的审核意见，显示默认提示 */}
              {!questionnaire?.reviewComments &&
                (!questionnaire?.rejectionReasons ||
                  questionnaire.rejectionReasons.length === 0) && (
                  <Text type="secondary">
                    {t('questionnaire:resubmit.noDetailedComments')}
                  </Text>
                )}
            </Card>
          </div>
        )}

        {/* 移动端顶部进度指示器 - 替代左侧进度 */}
        <div className="mobile-progress-header">
          <div className="progress-info">
            <span className="step-title">
              {!isReviewMode
                ? questionnaireSteps[currentStep]?.title
                : t('questionnaire:page.infoConfirm')}
            </span>
            <span className="step-counter">
              {!isReviewMode
                ? `${currentStep + 1}/${questionnaireSteps.length}`
                : `${questionnaireSteps.length + 1}/${questionnaireSteps.length + 1}`}
            </span>
          </div>
          <Progress
            percent={stepProgress}
            strokeColor={{
              '0%': '#10b981',
              '100%': '#059669',
            }}
            trailColor="#e5e7eb"
            strokeWidth={8}
            showInfo={false}
            style={{ marginBottom: 8 }}
          />
          <div
            style={{ textAlign: 'center', fontSize: '13px', color: '#6b7280' }}
          >
            {!isReviewMode
              ? questionnaireSteps[currentStep]?.description
              : t('questionnaire:page.confirmInfo')}
          </div>
        </div>

        <Row gutter={[24, 24]}>
          {/* 左侧进度指示 - 桌面端显示，移动端隐藏 */}
          <Col xs={0} lg={6} className="questionnaire-progress-sidebar">
            <Card
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                position: 'sticky',
                top: '24px',
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <div style={{ textAlign: 'center' }}>
                  <Progress
                    type="circle"
                    percent={stepProgress}
                    size={120}
                    strokeColor={{
                      '0%': '#10b981',
                      '100%': '#059669',
                    }}
                    format={percent => (
                      <div style={{ textAlign: 'center' }}>
                        <div
                          style={{
                            fontSize: '24px',
                            fontWeight: 'bold',
                            color: '#10b981',
                          }}
                        >
                          {percent}%
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {t('questionnaire:page.completion')}
                        </div>
                      </div>
                    )}
                  />
                </div>

                <Steps
                  direction="vertical"
                  size="small"
                  current={
                    isReviewMode ? questionnaireSteps.length : currentStep
                  }
                >
                  {questionnaireSteps.map((step, index) => (
                    <Step
                      key={step.key}
                      title={step.title}
                      description={step.description}
                      icon={
                        index < currentStep ? (
                          <CheckCircleOutlined style={{ color: '#10b981' }} />
                        ) : index === currentStep ? (
                          <ClockCircleOutlined style={{ color: '#10b981' }} />
                        ) : undefined
                      }
                    />
                  ))}
                  {isReviewMode && (
                    <Step
                      title={t('questionnaire:page.infoConfirm')}
                      description={t('questionnaire:page.confirmAndSubmit')}
                      icon={
                        <ClockCircleOutlined style={{ color: '#10b981' }} />
                      }
                    />
                  )}
                </Steps>
              </Space>
            </Card>
          </Col>

          {/* 右侧问卷内容 */}
          <Col xs={24} lg={18} className="questionnaire-content-area">
            <Card
              className="questionnaire-card"
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                minHeight: '600px',
              }}
              bodyStyle={{ padding: '32px' }}
            >
              {!isReviewMode && (
                <div style={{ marginBottom: '32px' }}>
                  <Title
                    level={3}
                    style={{ color: '#1f2937', marginBottom: '8px' }}
                  >
                    {questionnaireSteps[currentStep]?.title}
                  </Title>
                  <Paragraph
                    style={{
                      color: '#6b7280',
                      fontSize: '16px',
                      marginBottom: '16px',
                    }}
                  >
                    {questionnaireSteps[currentStep]?.description}
                  </Paragraph>
                </div>
              )}

              <Form
                form={form}
                layout="vertical"
                size="large"
                className="questionnaire-form"
                key={questionnaire?.id || questionnaire?._id || 'new'}
                initialValues={questionnaire}
                onValuesChange={useCallback(
                  (_: any, allValues: any) => {
                    updateQuestionnaire(allValues);
                  },
                  [updateQuestionnaire]
                )}
              >
                {isLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '400px',
                    }}
                  >
                    <Spin size="large" />
                  </div>
                ) : (
                  renderStepContent()
                )}
              </Form>
            </Card>
          </Col>
        </Row>
      </div>

      {/* 底部操作按钮 */}
      {renderActionButtons()}

      {/* AI评分弹窗 */}
      <Modal
        title={
          <Space>
            <TrophyOutlined style={{ color: '#10b981' }} />
            <span>{t('questionnaire:aiScore.title')}</span>
          </Space>
        }
        open={showAIScoreModal}
        onCancel={() => setShowAIScoreModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowAIScoreModal(false)}>
            {t('questionnaire:button.close')}
          </Button>,
        ]}
        width={600}
        centered
      >
        {aiScore !== null && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div
              style={{
                fontSize: '48px',
                fontWeight: 'bold',
                color:
                  aiScore >= 80
                    ? '#52c41a'
                    : aiScore >= 60
                      ? '#faad14'
                      : '#ff4d4f',
                marginBottom: '16px',
              }}
            >
              {t('questionnaire:aiScore.score', { score: aiScore })}
            </div>
            <div
              style={{ fontSize: '16px', color: '#666', marginBottom: '24px' }}
            >
              {aiScore >= 80 && t('questionnaire:aiScore.excellent')}
              {aiScore >= 60 && aiScore < 80 && t('questionnaire:aiScore.good')}
              {aiScore < 60 && t('questionnaire:aiScore.low')}
            </div>
            <Alert
              message={t('questionnaire:aiScore.title')}
              description={t('questionnaire:aiScore.description')}
              type="info"
              showIcon
              style={{ textAlign: 'left' }}
            />
          </div>
        )}
      </Modal>

      {/* 用户注册成功模态框 - 邮件激活提示 */}
      <Modal
        open={showRegistrationModal}
        onCancel={() => {
          setShowRegistrationModal(false);
          navigate('/auth/login');
        }}
        footer={null}
        width={600}
        centered
        closable={false}
        styles={{
          body: { padding: '0' },
          content: {
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          },
        }}
      >
        {autoRegisterResult && (
          <div
            style={{
              background:
                'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* 装饰性背景图案 */}
            <div
              style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '200px',
                height: '200px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '50%',
                transform: 'rotate(45deg)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: '-30px',
                left: '-30px',
                width: '150px',
                height: '150px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '50%',
              }}
            />

            {/* 头部成功区域 */}
            <div
              style={{
                padding: '40px 40px 30px',
                textAlign: 'center',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <div
                style={{
                  width: '80px',
                  height: '80px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <CheckCircleOutlined
                  style={{
                    fontSize: '40px',
                    color: '#ffffff',
                  }}
                />
              </div>

              <Title
                level={2}
                style={{
                  color: '#ffffff',
                  margin: '0 0 8px 0',
                  fontWeight: 700,
                  fontSize: '28px',
                }}
              >
                {t('questionnaire:success.title')}
              </Title>

              <Text
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '16px',
                  display: 'block',
                  marginBottom: '16px',
                }}
              >
                {t('questionnaire:success.accountCreated', {
                  company: autoRegisterResult.data.user.companyName,
                })}
              </Text>
            </div>

            {/* 主要内容区域 */}
            <div
              style={{
                background: '#ffffff',
                padding: '32px 40px 40px',
                borderRadius: '20px 20px 0 0',
                marginTop: '-20px',
                position: 'relative',
                zIndex: 2,
              }}
            >
              {/* 邮件激活提示 */}
              <div
                style={{
                  background:
                    'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                  borderRadius: '16px',
                  padding: '24px',
                  marginBottom: '24px',
                  border: '1px solid #10b981',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    width: '60px',
                    height: '60px',
                    background:
                      'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                  }}
                >
                  <span style={{ fontSize: '28px' }}>📧</span>
                </div>
                <Title
                  level={4}
                  style={{ margin: '0 0 8px 0', color: '#065f46' }}
                >
                  {t('questionnaire:success.checkEmail')}
                </Title>
                <Text style={{ color: '#047857', fontSize: '15px' }}>
                  {t('questionnaire:success.emailSent')}
                </Text>
              </div>

              {/* 账户信息 */}
              <div
                style={{
                  background: '#f8fafc',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '20px',
                  border: '1px solid #e2e8f0',
                }}
              >
                <div style={{ display: 'grid', gap: '12px' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{ color: '#64748b' }}>
                      {t('questionnaire:success.loginAccount')}
                    </Text>
                    <Text strong style={{ color: '#1e293b' }}>
                      {autoRegisterResult.data.registration.username}
                    </Text>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{ color: '#64748b' }}>
                      {t('questionnaire:success.receiveEmail')}
                    </Text>
                    <Text strong style={{ color: '#1e293b' }}>
                      {autoRegisterResult.data.registration.email}
                    </Text>
                  </div>
                </div>
              </div>

              {/* 重要提示 */}
              <div
                style={{
                  background: '#fffbeb',
                  borderRadius: '12px',
                  padding: '16px 20px',
                  marginBottom: '24px',
                  border: '1px solid #fcd34d',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <ExclamationCircleOutlined
                    style={{
                      color: '#d97706',
                      fontSize: '18px',
                      marginRight: '12px',
                      marginTop: '2px',
                    }}
                  />
                  <div>
                    <Text
                      strong
                      style={{
                        color: '#92400e',
                        display: 'block',
                        marginBottom: '6px',
                        fontSize: '14px',
                      }}
                    >
                      {t('questionnaire:tips.important')}
                    </Text>
                    <div
                      style={{
                        color: '#92400e',
                        lineHeight: '1.6',
                        fontSize: '13px',
                      }}
                    >
                      <div>• {t('questionnaire:tips.activateLink')}</div>
                      <div>• {t('questionnaire:tips.linkValid')}</div>
                      <div>• {t('questionnaire:tips.passwordInEmail')}</div>
                      <div>• {t('questionnaire:tips.checkSpam')}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 操作按钮 */}
              <Button
                type="primary"
                size="large"
                block
                onClick={() => {
                  setShowRegistrationModal(false);
                  navigate('/auth/login');
                }}
                style={{
                  background:
                    'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  borderColor: 'transparent',
                  borderRadius: '12px',
                  height: '52px',
                  fontWeight: 600,
                  fontSize: '16px',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
                }}
              >
                {t('questionnaire:button.goToLogin')}
              </Button>

              <Text
                type="secondary"
                style={{
                  display: 'block',
                  textAlign: 'center',
                  marginTop: '16px',
                  fontSize: '13px',
                }}
              >
                {t('questionnaire:tips.afterActivation')}
              </Text>
            </div>
          </div>
        )}
      </Modal>

      {/* 离开页面提醒 */}
      {hasUnsavedChanges && (
        <div style={{ display: 'none' }}>
          {/* 这里可以添加beforeunload事件监听 */}
        </div>
      )}
    </div>
  );
};

export default InitialScreeningQuestionnaireComponent;
